'use client'

import { useState, useRef, useEffect, forwardRef, type ReactNode } from 'react'
import { AnimatePresence } from 'motion/react'
import Toast from '@/components/ui/Toast'
import Button from '@/components/ui/Button'
import Tooltip from '@/components/ui/Tooltip'
import { Icon } from '@/icons/Icon'
import { cn, toSlug, scrollToSection } from '@/lib/utils'
import { useBreakpoint } from '@/hooks/useBreakpoint'

type SectionProps = {
  children: ReactNode
  title: string
  header?: ReactNode
  titleBadge?: ReactNode
  collapsed?: boolean
  className?: string
  isParent?: boolean
  isChild?: boolean
  icon?: ReactNode
  offset?: number
  join?: boolean
  copy?: boolean
  tooltip?: boolean
  sectionId?: string
  depth?: 'root' | 'branch' | 'leaf'
  rootTag?: number
  ignoreHeading?: boolean
  toggle?: boolean
  renderCollapsedContent?: boolean
}

const Section = forwardRef<HTMLDivElement, SectionProps>(
  (
    {
      children,
      title,
      header,
      titleBadge,
      className,
      isParent,
      isChild,
      offset = 154,
      icon,
      collapsed = false,
      join = false,
      copy = true,
      sectionId,
      depth,
      rootTag,
      ignoreHeading = false,
      toggle = true,
      renderCollapsedContent = true,
    },
    ref,
  ) => {
    const initialDepth = isChild ? 3 : 2

    const getTagLevel = (): number => {
      if (!depth) return Math.min(6, Math.max(1, initialDepth))

      const baseLevel = Math.min(6, Math.max(1, rootTag || 2))
      const depthOffset = {
        root: 0,
        branch: 1,
        leaf: 2,
      }[depth]

      return Math.min(6, baseLevel + depthOffset)
    }

    const Tag = ignoreHeading
      ? 'div'
      : (`h${getTagLevel()}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6')

    const [expanded, setExpanded] = useState<boolean>(!collapsed)
    const [allExpanded, setAllExpanded] = useState<boolean>(!collapsed)
    const [toast, setToast] = useState<string | null>(null)

    const content = useRef<HTMLDivElement>(null)
    const sectionRef = useRef<HTMLDivElement>(null)

    // Sync expanded state when parent changes the collapsed prop
    // (e.g., navigating between compare pages with different primary categories)
    useEffect(() => {
      setExpanded(!collapsed)
    }, [collapsed])

    // Sync allExpanded with actual child states when parent expands
    useEffect(() => {
      if (!isParent || !expanded || !sectionRef.current) return

      const checkChildStates = () => {
        const childSections = sectionRef.current?.querySelectorAll('[data-section-child="true"]')
        if (!childSections || childSections.length === 0) return

        const allChildrenExpanded = Array.from(childSections).every(
          (child) => (child as HTMLElement).dataset.expanded === 'true',
        )

        if (allChildrenExpanded !== allExpanded) {
          setAllExpanded(allChildrenExpanded)
        }
      }

      // Check after a short delay to ensure children have mounted
      const timer = setTimeout(checkChildStates, 50)
      return () => clearTimeout(timer)
    }, [expanded, isParent])

    const { md } = useBreakpoint()
    const responsiveOffset = offset + (!md ? (toggle ? 60 : 55) : 0)
    const resolvedId = sectionId || toSlug(title)
    const shouldRenderContent = expanded || renderCollapsedContent

    useEffect(() => {
      // Handle initial hash scroll on page load
      const sectionHash = `#${resolvedId}`
      const shouldScrollToSection = window.location.hash === sectionHash

      if (shouldScrollToSection) {
        // Ensure section is expanded
        setExpanded(true)

        const timer = setTimeout(() => {
          const element = document.getElementById(resolvedId)
          if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY - responsiveOffset
            window.scrollTo({ top: y, behavior: 'auto' })
          }
        }, 0)
        return () => clearTimeout(timer)
      }

      const handleToggle = (e: CustomEvent) => {
        setExpanded(e.detail.expanded)
      }

      // Only child sections should listen for toggleSection events from their parent
      if (sectionRef.current && isChild) {
        sectionRef.current.addEventListener('toggleSection', handleToggle as EventListener)
      }

      return () => {
        if (sectionRef.current && isChild) {
          sectionRef.current.removeEventListener('toggleSection', handleToggle as EventListener)
        }
      }
    }, [responsiveOffset, resolvedId])

    useEffect(() => {
      if (toast) {
        const timer = setTimeout(() => setToast(null), 3000)
        return () => clearTimeout(timer)
      }
    }, [toast])

    const copyLink = (id: string) => {
      const currentUrl = `${window.location.origin}${window.location.pathname}`
      navigator.clipboard.writeText(`${currentUrl}#${id}`)
      setToast('Link copied to clipboard')
    }

    const toggleExpanded = () => {
      setExpanded(!expanded)
      if (sectionRef.current) {
        sectionRef.current.dataset.expanded = (!expanded).toString()
      }
    }

    const toggleAll = () => {
      const newState = !allExpanded
      setAllExpanded(newState)

      if (sectionRef.current) {
        const childSections = sectionRef.current.querySelectorAll('[data-section-child="true"]')
        childSections.forEach((section: Element) => {
          const sectionComponent = section as HTMLDivElement
          const event = new CustomEvent('toggleSection', { detail: { expanded: newState } })
          sectionComponent.dispatchEvent(event)
        })
      }
    }

    return (
      <section
        ref={(el: HTMLDivElement | null) => {
          if (typeof ref === 'function') ref(el)
          else if (ref) ref.current = el
          sectionRef.current = el
        }}
        id={resolvedId}
        data-name={title}
        data-section-id={resolvedId}
        data-section-child={isChild}
        data-section-parent={isParent}
        data-expanded={expanded}
        className={cn(
          'w-full border',
          'border-gray-200 bg-white dark:border-zinc-800 dark:bg-black dark:text-zinc-300',
          join ? 'border-t-0 first:rounded-t-2xl first:border-t last:rounded-b-2xl' : 'rounded-2xl',
          !expanded && 'border-b-0',
          className,
        )}
        style={{ scrollMarginTop: `${responsiveOffset}px` }}
      >
        <div
          className={cn(
            'header group relative flex w-full cursor-pointer items-center justify-between gap-2 border-b py-4 pl-5 pr-2.5',
            join
              ? {
                  'group-first:rounded-t-2xl': true,
                  'group-last:rounded-b-2xl': !expanded,
                }
              : {
                  'rounded-t-2xl': true,
                  'rounded-b-2xl': !expanded,
                },
            isChild ? 'h-12' : 'h-16',
            'border-gray-200 bg-zinc-50 dark:border-zinc-800 dark:bg-white/5',
          )}
        >
          <div className="relative z-30 flex items-center gap-3">
            {icon && (
              <div className={cn('-mt-0.5 flex items-center text-gray-600 dark:text-primary-500')}>
                {icon}
              </div>
            )}
            <div onClick={toggleExpanded} className="flex items-center gap-3">
              <Tooltip text={title} delay={true} position="up" wrap={true}>
                <Tag
                  className={cn(
                    'line-clamp-2 w-fit cursor-pointer text-14 font-bold leading-tight text-gray-950 dark:text-white md:line-clamp-1 md:text-16',
                    !isChild && 'text-16 md:text-18 md:leading-tight',
                  )}
                >
                  {title}
                </Tag>
              </Tooltip>
              {titleBadge}
            </div>
          </div>
          {header ? (
            <div className="relative z-30 ml-auto">{header}</div>
          ) : (
            <div className="relative z-30 ml-auto hidden gap-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:flex">
              {copy && (
                <Tooltip text="Copy Link" delay={true} position="down">
                  <Button
                    onClick={() => {
                      copyLink(resolvedId)
                      scrollToSection({ id: resolvedId, offset: responsiveOffset, animate: true })
                    }}
                  >
                    <Icon name="hyperlink" size={12} />
                  </Button>
                </Tooltip>
              )}
              {isParent && (
                <Tooltip text={allExpanded ? 'Close All' : 'Open All'} delay={true} position="down">
                  <Button onClick={toggleAll}>
                    <Icon name={allExpanded ? 'chevron-reduce-y' : 'chevron-expand-y'} size={12} />
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
          <div
            onClick={toggleExpanded}
            className={cn(
              'relative z-30 flex h-8 w-8 -rotate-90 cursor-pointer items-center justify-center transition-all',
              'text-gray-600 group-hover:text-gray-600 dark:text-zinc-500 dark:group-hover:text-white',
              expanded && 'rotate-90',
            )}
          >
            <Icon name="chevron-left" size={12} />
          </div>

          <button
            onClick={toggleExpanded}
            aria-label={`Toggle ${title || 'section'}`}
            aria-expanded={expanded}
            className={cn(
              'absolute left-0 z-20 h-full w-full',
              header ? 'cursor-default' : 'cursor-pointer',
            )}
          ></button>
        </div>
        {shouldRenderContent && (
          <div
            data-section-content-visible={expanded}
            className={cn('-mt-px', !expanded && 'hidden')}
          >
            <div
              ref={content}
              className={cn(
                'rich-text flex flex-col gap-5 p-5',
                isChild ? 'md:px-5 md:py-4' : 'md:p-5',
              )}
            >
              {children}
            </div>
          </div>
        )}
        <AnimatePresence>{toast && <Toast>{toast}</Toast>}</AnimatePresence>
      </section>
    )
  },
)

Section.displayName = 'Section'

export default Section
